import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  getActiveContracts,
  getInactiveContracts,
  getTraders,
  getContractDetail,
} from "../../Services/banking/contracts";

// Helper for consistent error handling
const handleApiError = (err, rejectWithValue) => {
  return rejectWithValue(
    err.response?.data?.message || err.message || "Something went wrong",
  );
};

// discom_id is the logged-in discomId; state_id defaults to 6 in the service layer.

export const fetchActiveContracts = createAsyncThunk(
  "contracts/fetchActive",
  async ({ partyId, stateId }, { rejectWithValue }) => {
    try {
      const response = await getActiveContracts(partyId, stateId);
      return response?.data?.data || [];
    } catch (err) {
      return handleApiError(err, rejectWithValue);
    }
  },
);

export const fetchInactiveContracts = createAsyncThunk(
  "contracts/fetchInactive",
  async ({ partyId, stateId }, { rejectWithValue }) => {
    try {
      const response = await getInactiveContracts(partyId, stateId);
      return response?.data?.data || [];
    } catch (err) {
      return handleApiError(err, rejectWithValue);
    }
  },
);

export const fetchTraders = createAsyncThunk(
  "contracts/fetchTraders",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getTraders();
      return response?.data?.data?.traders || [];
    } catch (err) {
      return handleApiError(err, rejectWithValue);
    }
  },
);

// GET /banking_otc/contracts/detail?connection_id=...&state_id=...&discom_id=... — the matched contract for one connection.
export const fetchContractDetail = createAsyncThunk(
  "contracts/fetchDetail",
  async ({ connectionId, stateId, discomId }, { rejectWithValue }) => {
    try {
      const response = await getContractDetail(connectionId, stateId, discomId);
      const data = response?.data?.data;
      return Array.isArray(data) ? data[0] || null : data || null;
    } catch (err) {
      return handleApiError(err, rejectWithValue);
    }
  },
);

const initialState = {
  activeContracts: [],
  inactiveContracts: [],
  traders: [],
  contractDetail: null,

  activeContractsLoading: false,
  inactiveContractsLoading: false,
  tradersLoading: false,
  contractDetailLoading: false,

  activeContractsError: null,
  inactiveContractsError: null,
  tradersError: null,
  contractDetailError: null,
};

const contractsSlice = createSlice({
  name: "contracts",
  initialState,
  reducers: {
    clearContracts: () => initialState,
    clearContractDetail: (state) => {
      state.contractDetail = null;
      state.contractDetailError = null;
    },
  },
  extraReducers: (builder) => {
    // --- Active contracts ---
    builder
      .addCase(fetchActiveContracts.pending, (state) => {
        state.activeContractsLoading = true;
        state.activeContractsError = null;
      })
      .addCase(fetchActiveContracts.fulfilled, (state, action) => {
        state.activeContractsLoading = false;
        state.activeContracts = action.payload;
      })
      .addCase(fetchActiveContracts.rejected, (state, action) => {
        state.activeContractsLoading = false;
        state.activeContractsError = action.payload;
      });

    // --- Inactive contracts ---
    builder
      .addCase(fetchInactiveContracts.pending, (state) => {
        state.inactiveContractsLoading = true;
        state.inactiveContractsError = null;
      })
      .addCase(fetchInactiveContracts.fulfilled, (state, action) => {
        state.inactiveContractsLoading = false;
        state.inactiveContracts = action.payload;
      })
      .addCase(fetchInactiveContracts.rejected, (state, action) => {
        state.inactiveContractsLoading = false;
        state.inactiveContractsError = action.payload;
      });

    // --- Traders ---
    builder
      .addCase(fetchTraders.pending, (state) => {
        state.tradersLoading = true;
        state.tradersError = null;
      })
      .addCase(fetchTraders.fulfilled, (state, action) => {
        state.tradersLoading = false;
        state.traders = action.payload;
      })
      .addCase(fetchTraders.rejected, (state, action) => {
        state.tradersLoading = false;
        state.tradersError = action.payload;
      });

    // --- Contract detail (by connection_id) ---
    builder
      .addCase(fetchContractDetail.pending, (state) => {
        state.contractDetailLoading = true;
        state.contractDetailError = null;
      })
      .addCase(fetchContractDetail.fulfilled, (state, action) => {
        state.contractDetailLoading = false;
        state.contractDetail = action.payload;
      })
      .addCase(fetchContractDetail.rejected, (state, action) => {
        state.contractDetailLoading = false;
        state.contractDetailError = action.payload;
        state.contractDetail = null;
      });
  },
});

export const { clearContracts, clearContractDetail } = contractsSlice.actions;

export default contractsSlice.reducer;
